import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import Modal from './Modal';

/**
 * BeGateway Payment component that can be triggered from an external button
 * 
 * @param {Object} props
 * @param {string} props.orderId - Unique order identifier
 * @param {number} props.amount - Payment amount
 * @param {string} props.currency - Currency code (EUR, USD, etc)
 * @param {string} props.description - Payment description
 * @param {Object} props.customer - Customer information
 * @param {function} props.onSuccess - Callback for successful payment
 * @param {function} props.onError - Callback for payment errors
 * @param {boolean} props.hideButton - Whether to hide the default payment button
 */
const BeGatewayPayment = forwardRef(({
    orderId,
    amount,
    currency = 'EUR',
    description = 'Star Map Purchase',
    customer = {},
    onSuccess,
    onError,
    hideButton = false
}, ref) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [debugInfo, setDebugInfo] = useState(null);
    const [paymentToken, setPaymentToken] = useState(null);
    const [scriptLoaded, setScriptLoaded] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [widgetError, setWidgetError] = useState(false);
    const [paymentSuccessful, setPaymentSuccessful] = useState(false);
    const [transactionData, setTransactionData] = useState(null);
    const containerRef = useRef(null);
    const widgetContainerId = 'begateway-widget-container';

    // Expose methods to parent component via ref
    useImperativeHandle(ref, () => ({
        openPaymentModal: () => {
            if (!scriptLoaded || !paymentToken) {
                console.error('Payment system not ready yet');
                return false;
            }
            handleOpenModal();
            return true;
        },
        isReady: () => {
            return scriptLoaded && paymentToken !== null && !loading;
        },
        getPaymentUrl: () => {
            return paymentToken?.redirect_url || null;
        }
    }));

    // Get payment token from the server
    useEffect(() => {
        const fetchPaymentToken = async () => {
            try {
                setLoading(true);
                const paymentData = {
                    amount,
                    currency,
                    description,
                    order_id: orderId,
                    customer,
                    iframe_mode: hideButton
                };

                console.log('Attempting to fetch payment token with data:', paymentData);

                // Use the Slim framework API endpoint
                const response = await fetch('/api/payment/create-token', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(paymentData),
                });

                console.log('Payment token response status:', response.status);

                // Store raw response text for debugging
                const responseText = await response.text();
                console.log('Raw response:', responseText);

                // Try to parse as JSON
                let data;
                try {
                    data = JSON.parse(responseText);
                    console.log('Payment token response data:', data);
                } catch (parseError) {
                    console.error('Error parsing JSON response:', parseError);
                    throw new Error('Invalid response from payment server: ' + responseText.substring(0, 100) + '...');
                }

                if (!response.ok) {
                    // Store debug info for display
                    setDebugInfo({
                        requestData: paymentData,
                        responseStatus: response.status,
                        responseData: data
                    });
                    throw new Error(data.message || data.error || 'Failed to create payment token');
                }

                // Modify the redirect URL to use our iframe-specific success page
                let modifiedData = { ...data };
                if (data.redirect_url) {
                    // Use a unique token pattern in the URL to help identify successful redirects
                    const uniqueToken = `payment-complete-${Date.now()}`;
                    modifiedData.redirect_url = data.redirect_url.replace(
                        /\/payment\/success/,
                        `/payment-iframe-success?token=${uniqueToken}`
                    );
                    console.log('Modified redirect URL:', modifiedData.redirect_url);

                    // Store the token for later validation
                    window.paymentCompleteToken = uniqueToken;
                }

                // Store payment token data
                setPaymentToken(modifiedData);
                setLoading(false);
            } catch (err) {
                console.error('Error creating payment token:', err);
                setError(err.message || 'Failed to initialize payment');
                setLoading(false);
                if (onError) onError(err);
            }
        };

        if (orderId && amount) {
            fetchPaymentToken();
        } else if (!orderId || !amount) {
            setError('Missing required payment information');
        }
    }, [orderId, amount, currency, description, customer, onError, hideButton]);

    // Load BeGateway script
    useEffect(() => {
        if (scriptLoaded || document.getElementById('begateway-script')) {
            return;
        }

        // Remove any existing script
        const existingScript = document.getElementById('begateway-script');
        if (existingScript) {
            existingScript.remove();
        }

        const script = document.createElement('script');
        script.id = 'begateway-script';
        script.src = 'https://js.begateway.com/begateway-1-latest.min.js';
        script.async = true;

        script.onload = () => {
            console.log('BeGateway script loaded successfully');
            setScriptLoaded(true);
        };

        script.onerror = (err) => {
            console.error('Error loading BeGateway script:', err);
            setWidgetError(true);
        };

        document.body.appendChild(script);

        return () => {
            // Clean up on unmount
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
        };
    }, []);

    // Set up message listener for payment results
    useEffect(() => {
        const handleMessage = (event) => {
            // Skip irrelevant messages
            if (event.data.target === 'metamask-inpage') {
                return;
            }

            try {
                console.log('Received message event:', event);
                const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
                console.log('Parsed payment message data:', data);

                // Set success state and store transaction data 
                if (data.status === 'successful') {
                    console.log('Payment successful, showing success UI');
                    // Immediately replace the iframe with success message
                    setPaymentSuccessful(true);
                    setTransactionData(data);

                    // Clear the iframe container to avoid flashing content
                    const container = document.getElementById(widgetContainerId);
                    if (container) {
                        container.innerHTML = '';
                    }

                    // Immediately call success callback without delay
                    if (onSuccess) {
                        onSuccess(data);
                    }
                } else if (data.status === 'failed' && onError) {
                    console.log('Payment failed, calling onError callback with message:', data.message);
                    onError(new Error(data.message || 'Payment failed'));
                    setIsModalOpen(false);
                } else if (data.status === 'cancelled') {
                    console.log('Payment cancelled by user');
                    if (onError) onError(new Error('Payment cancelled by user'));
                    setIsModalOpen(false);
                }
            } catch (err) {
                console.error('Error processing payment message:', err);
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [onSuccess, onError, widgetContainerId]);

    // Add a URL change listener for the iframe to detect successful redirects
    useEffect(() => {
        // Function to check iframe URL for payment completion
        const checkIframeForPaymentCompletion = () => {
            // Only run if we're in the iframe payment mode and not already marked as successful
            if (!hideButton || paymentSuccessful || !document.getElementById(widgetContainerId)) return;

            try {
                const iframes = document.getElementById(widgetContainerId).querySelectorAll('iframe');
                if (iframes.length > 0) {
                    const iframe = iframes[0];

                    // Check if we can access the iframe's URL (if same origin)
                    try {
                        const iframeLocation = iframe.contentWindow.location.href;
                        console.log('Checking iframe URL:', iframeLocation);

                        // Check if the URL contains our success indicators
                        if (
                            iframeLocation.includes('payment-iframe-success') ||
                            iframeLocation.includes('payment/success') ||
                            (window.paymentCompleteToken && iframeLocation.includes(window.paymentCompleteToken))
                        ) {
                            console.log('Payment success detected via iframe URL!');

                            // Clear the iframe immediately to avoid flashing content
                            document.getElementById(widgetContainerId).innerHTML = '';

                            setPaymentSuccessful(true);
                            setTransactionData({
                                transaction_id: orderId,
                                status: 'successful'
                            });

                            // Call success callback immediately
                            if (onSuccess) {
                                onSuccess({
                                    transaction_id: orderId,
                                    status: 'successful'
                                });
                            }
                        }
                    } catch (e) {
                        // Cross-origin restriction - we can't access the URL directly
                        // This is expected and not an error
                    }

                    // Set up a mutation observer to detect changes to the iframe content
                    // BeGateway might replace the entire iframe after payment
                    const observer = new MutationObserver((mutations) => {
                        for (const mutation of mutations) {
                            if (mutation.type === 'childList' && mutation.addedNodes.length) {
                                // Check the added nodes for transaction success indicators
                                for (const node of mutation.addedNodes) {
                                    if (node.nodeType === Node.ELEMENT_NODE) {
                                        // Check for any elements that might indicate success
                                        const successElements = node.querySelectorAll('.begateway-payment-success, .success, [class*="success"]');
                                        const successTexts = ['success', 'complete', 'payment complete', 'thank you'];

                                        if (successElements.length > 0) {
                                            console.log('Payment success element detected in DOM!');

                                            // Clear the iframe immediately
                                            document.getElementById(widgetContainerId).innerHTML = '';

                                            setPaymentSuccessful(true);
                                            observer.disconnect();

                                            // Call success callback
                                            if (onSuccess) {
                                                onSuccess({
                                                    transaction_id: orderId,
                                                    status: 'successful'
                                                });
                                            }
                                            return;
                                        }

                                        // Check if any text content contains success indicators
                                        const textContent = node.textContent?.toLowerCase() || '';
                                        for (const text of successTexts) {
                                            if (textContent.includes(text)) {
                                                console.log('Payment success text detected in DOM:', text);

                                                // Clear the iframe immediately
                                                document.getElementById(widgetContainerId).innerHTML = '';

                                                setPaymentSuccessful(true);
                                                observer.disconnect();

                                                // Call success callback
                                                if (onSuccess) {
                                                    onSuccess({
                                                        transaction_id: orderId,
                                                        status: 'successful'
                                                    });
                                                }
                                                return;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    });

                    // Start observing the widget container for changes
                    observer.observe(document.getElementById(widgetContainerId), {
                        childList: true,
                        subtree: true,
                        characterData: true
                    });

                    return () => observer.disconnect();
                }
            } catch (err) {
                console.error('Error checking iframe for payment completion:', err);
            }
        };

        // Check immediately and set up interval to check frequently
        checkIframeForPaymentCompletion();
        const intervalId = setInterval(checkIframeForPaymentCompletion, 300);

        return () => clearInterval(intervalId);
    }, [hideButton, paymentSuccessful, orderId, onSuccess, widgetContainerId]);

    // Handle opening the payment modal (only for non-embedded mode)
    const handleOpenModal = () => {
        setIsModalOpen(true);
        setWidgetError(false); // Reset any previous errors
    };

    // Handle direct link as fallback
    const handleDirectLinkClick = (e) => {
        e.preventDefault();

        if (!paymentToken || !paymentToken.redirect_url) {
            console.error('Cannot navigate to payment: missing redirect URL');
            setError('Payment URL not available. Please try again.');
            return;
        }

        // Open in new tab
        window.open(paymentToken.redirect_url, '_blank', 'noopener,noreferrer');
    };

    // Initialize payment form directly in the container (without modal)
    const initializePaymentForm = () => {
        try {
            // Check if BeGateway constructor exists
            if (typeof window.BeGateway !== 'function') {
                throw new Error('BeGateway payment form could not be initialized');
            }

            console.log('Creating inline BeGateway payment form with URL:', paymentToken.redirect_url);

            // First clear the container
            const container = document.getElementById(widgetContainerId);
            if (container) {
                // Clear the container
                container.innerHTML = '';

                // Create and configure the BeGateway widget with inline type for embedding
                const paymentWidget = new window.BeGateway({
                    type: 'inline',  // Use inline type for embedding in our own modal
                    id: widgetContainerId,
                    url: paymentToken.redirect_url,
                    style: "width: 100% !important; height: 600px; border: none; max-width: 100%; display: block;", // Force 100% width
                    size: { width: '100%', height: 600 }
                });

                // Build the widget form (embeds it in the container)
                paymentWidget.buildForm();

                // Force iframe to be 100% width after it's loaded
                const applyFullWidth = () => {
                    const iframes = container.querySelectorAll('iframe');
                    iframes.forEach(iframe => {
                        iframe.style.width = '100%';
                        iframe.style.maxWidth = '100%';
                        iframe.style.display = 'block';
                    });

                    // Also apply to any nested elements
                    const externalContainer = document.getElementById('external-widget-container');
                    if (externalContainer) {
                        externalContainer.style.width = '100%';
                        externalContainer.style.maxWidth = '100%';
                    }
                };

                // Try multiple times as the iframe may load asynchronously
                setTimeout(applyFullWidth, 500);
                setTimeout(applyFullWidth, 1000);
                setTimeout(applyFullWidth, 2000);

                console.log('Inline payment form built successfully');
            } else {
                console.error('Widget container not found:', widgetContainerId);
                setWidgetError(true);
            }
        } catch (err) {
            console.error('Error initializing inline payment form:', err);
            setWidgetError(true);
        }
    };

    // Auto-open payment modal when payment is ready (if hideButton is true)
    useEffect(() => {
        // When the component is ready and we're in embedded mode (hideButton=true)
        if (hideButton && scriptLoaded && paymentToken !== null && !loading && !paymentSuccessful) {
            // For embedded mode, don't open a modal but initialize the payment form directly
            initializePaymentForm();
        } else if (!hideButton && scriptLoaded && paymentToken !== null && !loading && !isModalOpen) {
            // Only open modal for non-embedded mode
            handleOpenModal();
        }
    }, [scriptLoaded, paymentToken, loading, hideButton, isModalOpen, paymentSuccessful]);

    // Render success message UI when payment is successful
    const renderSuccessMessage = () => {
        // Return null instead of success message - let the parent component handle success display
        return null;
    };

    if (error) {
        return (
            <div className="bg-red-900/20 border border-red-500 rounded-lg p-4 text-center">
                <p className="text-red-400">{error}</p>

                {debugInfo && (
                    <div className="mt-4 text-left bg-gray-900/50 p-3 rounded text-xs text-gray-300 overflow-auto max-h-40">
                        <h4 className="font-bold mb-1">Debug Information:</h4>
                        <p>Request Data:</p>
                        <pre>{JSON.stringify(debugInfo.requestData, null, 2)}</pre>
                        <p>Response Status: {debugInfo.responseStatus}</p>
                        <pre>{JSON.stringify(debugInfo.responseData, null, 2)}</pre>
                    </div>
                )}

                <button
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg"
                    onClick={() => window.location.reload()}
                >
                    Try Again
                </button>
            </div>
        );
    }

    // For the embedded widget container
    if (hideButton) {
        return (
            <div className="payment-container w-full" ref={containerRef}>
                {loading ? (
                    <div className="text-center py-10">
                        <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500"></div>
                        <p className="mt-4 text-lg text-gray-300">Initializing payment gateway...</p>
                    </div>
                ) : paymentSuccessful ? (
                    renderSuccessMessage()
                ) : (
                    <div id={widgetContainerId} className="payment-widget-container min-h-[600px] w-full" style={{ width: '100%', maxWidth: '100%' }}>
                        <div className="flex items-center justify-center h-full w-full">
                            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500"></div>
                            <p className="ml-4 text-lg text-gray-300">Loading payment form...</p>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Non-embedded mode (with buttons)
    return (
        <div className="payment-container w-full" ref={containerRef}>
            {loading ? (
                <div className="text-center py-10">
                    <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500"></div>
                    <p className="mt-4 text-lg text-gray-300">Initializing payment gateway...</p>
                </div>
            ) : (
                <div className="payment-form-container py-4 space-y-4">
                    <button
                        onClick={handleOpenModal}
                        className="w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg text-center transition-colors"
                        disabled={!scriptLoaded}
                    >
                        Complete Payment
                    </button>

                    <div className="flex justify-center">
                        <a
                            href="#"
                            onClick={handleDirectLinkClick}
                            className="text-sm text-gray-400 hover:text-white underline"
                        >
                            Open payment in new tab
                        </a>
                    </div>

                    <p className="text-center text-gray-300 mt-2">
                        Your payment is secured using industry-standard encryption.
                    </p>

                    {/* Modal for non-embedded mode */}
                    <Modal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        title="Complete Your Payment"
                        size="xl"
                    >
                        {widgetError ? (
                            <div className="text-center py-8">
                                <p className="text-yellow-400 mb-6">
                                    There was a problem loading the payment form. Please try using the direct payment link.
                                </p>
                                <button
                                    onClick={handleDirectLinkClick}
                                    className="py-3 px-6 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
                                >
                                    Continue to Payment in New Tab
                                </button>
                            </div>
                        ) : paymentSuccessful ? (
                            renderSuccessMessage()
                        ) : (
                            <div id={widgetContainerId} className="payment-widget-container min-h-[600px]">
                                <div className="flex items-center justify-center h-full">
                                    <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500"></div>
                                    <p className="ml-4 text-lg text-gray-300">Loading payment form...</p>
                                </div>
                            </div>
                        )}
                    </Modal>
                </div>
            )}
        </div>
    );
});

export default BeGatewayPayment; 